// NPM MODULES
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

// MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// COMPONENTS
import ContactImporter from '../../../common/ContactImporter';
import Loader from '../../../common/Loader';

// NETWORKING
import useAddressBookRequest from '../../../../requests/AddressBookRequest';

const DistributionAddEdit = ({
  errorHandler,
  backFunction,
  accessLevel,
  accessType,
  selected,
  setModalWidth,
  personal,
  global,
}) => {
  const [error, setError] = useState('');
  const [formFields, setFormFields] = useState({
    list_name: selected?.list_name ?? '',
    list_type: selected?.list_type ?? '',
    contactsToAdd: [],
    contactsToRemove: [],
  });
  const [saving, setSave] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allContacts, setContacts] = useState([]);
  const [currentContacts, setCurrentContacts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [addContacts, setAddContacts] = useState(false);
  const [listType, setListType] = useState('');
  const { getDistListContacts, getSmsContacts, editDistList, addDistList } =
    useAddressBookRequest();

  console.log('form:', formFields);
  console.log('selected:', selected);

  useEffect(() => {
    // Gets contact data to add contacts to list
    async function getData() {
      if (selected) {
        const currentData = await getDistListContacts(
          selected.distribution_list_id,
          errorHandler
        );
        console.log('current contacts:', currentData);
        setCurrentContacts(currentData);
      }
      const allContacts = await getSmsContacts(errorHandler, {
        distListId: selected?.distribution_list_id,
      });
      console.log('contacts for adding to lists:', allContacts);
      setContacts(allContacts);
      setLoading(false);
    }

    getData();
  }, []);

  useEffect(() => {
    if (selected?.list_type) return setListType(selected.list_type);
    if (accessLevel <= '0' || accessType === '0')
      return setListType('personal');
    if (global) return setListType('global');
    if (personal) return setListType('personal');
  }, [selected, global, accessLevel, personal]);

  const setModalSize = useCallback(() => {
    if (addContacts) setModalWidth(1000);
  }, [addContacts]);

  useEffect(() => {
    setModalSize();

    return () => {
      setModalWidth(450);
    };
  }, [setModalSize]);

  /**
   * Convenience function that renders toggle buttons.
   * @returns
   */
  const generateRadioButtons = () => {
    return (
      <FormControl component="fieldset">
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
          }}
        >
          <FormLabel sx={{ minWidth: 100 }} required>
            List Type
          </FormLabel>

          <RadioGroup
            row
            aria-label="contactType"
            name="contact_type"
            value={formFields.list_type}
            onChange={handleToggle}
            disabled={handleAccessLevel()}
          >
            <FormControlLabel
              value="personal"
              control={
                <Radio
                  color="secondary"
                  checked={formFields.list_type === 'personal'}
                />
              }
              label={AddressBookFormattedMessages.personal}
            />
            <FormControlLabel
              value="global"
              control={
                <Radio
                  color="secondary"
                  checked={formFields.list_type === 'global'}
                />
              }
              label={AddressBookFormattedMessages.global}
              disabled={accessLevel > '0' && accessType !== '0' ? false : true}
            />
          </RadioGroup>
        </Stack>
      </FormControl>
    );
  };

  /*
    Handles state changes from the Form
  */
  const handleChangeEvent = (e) => {
    const target = e.target;
    const name = target.name;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    if (name === 'email') {
      value = value.toLowerCase();
    }

    return setFormFields({
      ...formFields,
      [name]: value,
    });
  };

  /*
      Handles access level & type for read only view
    */
  const handleAccessLevel = () => {
    if (selected && formFields.list_type === 'Global')
      return accessLevel <= '0' && accessType === '0';
  };

  const handleToggle = (event) => {
    const { target } = event;

    console.log('set list type:', target.value);

    setListType(target.value);
    return setFormFields({
      ...formFields,
      list_type: target.value,
    });
  };

  /*
    Handles when the Form is submitted
  */
  const handleSubmit = async () => {
    // If not already saving...
    if (!saving) {
      let { list_name, list_type } = formFields;

      // Needs entry_name
      if ((!list_name && !selected) || (selected && list_name === '')) {
        return setError('List Name is required');
      }

      if (!list_type && !selected) {
        return setError('List Type is required');
      }

      const combinedContacts = currentContacts.concat(formFields.contactsToAdd);
      if (combinedContacts.length === 0) {
        return setError('Distribution List must have contacts');
      }

      setSave(true);
      setError('');
      let response;
      if (selected) {
        response = await editDistList(
          formFields,
          selected.distribution_list_id,
          setError
        );
      } else {
        response = await addDistList(formFields, setError);
      }
      setSave(false);

      if (response && !response?.error && !response?.message) {
        backFunction(true);
      }
    }
  };

  const handleButtonGroup = () => {
    // if customer user is add new contacts
    // if above three, show SAVE and CANCEL button
    return (
      <Stack
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'end',
          marginTop: 2,
          width: '100%',
          flexDirection: 'row',
        }}
      >
        <Button variant="link" color="secondary" onClick={backFunction}>
          {AddressBookFormattedMessages.cancelText}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setAddContacts(true)}
          disabled={!formFields.list_name || !formFields.list_type}
        >
          Add Contacts
        </Button>
      </Stack>
    );
  };

  /*
    Handles any errors generated
  */
  const generateError = () => {
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    // Return no error if none present
    return null;
  };

  const handleCheckboxChange = (value) => {
    setFormFields((state) => ({
      ...state,
      contactsToAdd: formFields.contactsToAdd.some(
        (item) => item.sms_contact_id === value
      )
        ? state.contactsToAdd.filter((item) => item.sms_contact_id !== value)
        : [...state.contactsToAdd, { sms_contact_id: value }],
    }));
  };

  const importNewContacts = (value) => {
    const newContacts = [...formFields.contactsToAdd, ...value];
    setFormFields({ ...formFields, contactsToAdd: newContacts });
  };

  const generateContactsTable = () => {
    const options = allContacts
      // Filter to make sure global lists cant have personal contacts
      .filter((contact) => {
        if (contact.contact_type === 'personal') {
          if (
            (selected && selected.list_type === 'global') ||
            (!selected && formFields.list_type === 'global')
          ) {
            return false;
          }
        }
        return true;
      });

    const rows = options.map((smsContact, i) => {
      return (
        <TableRow key={`add_${smsContact.sms_contact_id}`}>
          <TableCell>
            <Checkbox
              disabled={handleAccessLevel()}
              value={smsContact.sms_contact_id}
              onClick={() => handleCheckboxChange(smsContact.sms_contact_id)}
              checked={formFields.contactsToAdd.some(
                (item) => item.sms_contact_id === smsContact.sms_contact_id
              )}
              sx={{ padding: 0 }}
            />
          </TableCell>
          <TableCell>{smsContact.contact_name || smsContact.label}</TableCell>
          <TableCell>{smsContact.contact_email}</TableCell>
          <TableCell>{smsContact.contact_tn}</TableCell>
          <TableCell>
            {smsContact.contact_type === 'global'
              ? 'Global'
              : smsContact.contact_type === 'personal'
              ? 'Personal'
              : 'Neither'}
          </TableCell>
        </TableRow>
      );
    });

    return (
      <TableContainer
        sx={{
          marginBottom: 4,
          overflowX: 'auto',
          width: '100%',
          maxHeight: 350,
        }}
      >
        <Table variant="rounded">
          <TableHead>
            <TableRow key="head">
              <TableCell>Select</TableCell>
              <TableCell>{AddressBookFormattedMessages.nameText}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {AddressBookFormattedMessages.emailText}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {AddressBookFormattedMessages.mobileText}
              </TableCell>
              <TableCell>{AddressBookFormattedMessages.type}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) return <Loader />;

  if (importing)
    return (
      <ContactImporter
        handleBackClick={() => setImporting(false)}
        errorHandler={errorHandler}
        importType={selected?.list_type}
        setNewContacts={importNewContacts}
      />
    );

  if (addContacts) {
    return (
      <Stack>
        {generateContactsTable()}
        {generateError()}
        <Stack sx={{ alignItems: 'end', gap: 2 }}>
          <Typography sx={{ color: 'grey.700' }}>
            {formFields.contactsToAdd.length} Contacts Selected
          </Typography>

          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            <Button variant="link" color="secondary" onClick={backFunction}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving
                ? AddressBookFormattedMessages.saving
                : selected
                ? AddressBookFormattedMessages.updateList
                : AddressBookFormattedMessages.createList}
            </Button>
          </Stack>
          <Stack sx={{ flexDirection: { sm: 'row' }, gap: { sm: 2 } }}>
            <Typography sx={{ color: 'grey.700', textTransform: 'capitalize' }}>
              <strong>List Type:</strong>{' '}
              {formFields.list_type ?? selected.list_type}
            </Typography>
            <Typography sx={{ color: 'grey.700' }}>
              <strong>List Name:</strong>{' '}
              {formFields.list_name ?? selected.list_name}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box component="form">
      <Grid container>
        {generateRadioButtons()}
        <Grid size={12}>
          <Stack
            sx={{
              flexDirection: { md: 'row' },
              alignItems: { md: 'center' },
              gap: { md: 2 },
            }}
          >
            <InputLabel sx={{ minWidth: 100 }} required>
              List Name
            </InputLabel>

            <TextField
              name="list_name"
              defaultValue={selected ? selected.list_name : ''}
              onChange={handleChangeEvent}
              fullWidth
              size="small"
            />
          </Stack>
        </Grid>
        {generateError()}
        {handleButtonGroup()}
      </Grid>
    </Box>
  );
};

export default DistributionAddEdit;
